import type {
  CallsRuntimeIncident,
} from './CallsRuntimeIncidentEngine';

export type CallsResponseAction =
  | 'inspect-signaling-path'
  | 'inspect-media-path'
  | 'inspect-notification-path'
  | 'inspect-session-lifecycle'
  | 'observe-next-attempt';

export type CallsResponseAuthority =
  | 'Calls Governor'
  | 'Repair Governor'
  | 'Verification Governor';

export type CallsResponsePlanStatus =
  | 'planned'
  | 'awaiting-authority'
  | 'awaiting-verification';

export type CallsIncidentResponsePlan = Readonly<{
  id: string;
  incidentId: string;
  incidentKey: string;
  owner: 'Calls Governor';
  status: CallsResponsePlanStatus;
  recommendedAction: CallsResponseAction;
  requiredAuthority: CallsResponseAuthority;
  executionAllowed: false;
  retryEligible: boolean;
  escalationOwner: CallsResponseAuthority;
  verificationRequired: true;
  reasonCode: string;
  createdAt: string;
}>;

type UnknownRecord = Record<string, unknown>;

const asRecord = (
  value: unknown,
): UnknownRecord =>
  value !== null && typeof value === 'object'
    ? (value as UnknownRecord)
    : {};

const asString = (
  value: unknown,
  fallback: string,
): string =>
  typeof value === 'string' &&
  value.trim().length > 0
    ? value
    : fallback;

const selectAction = (
  incident: CallsRuntimeIncident,
): CallsResponseAction => {
  const signal = [
    incident.reasonCode,
    incident.source,
    incident.outcome,
  ]
    .join(' ')
    .toLowerCase();

  if (
    signal.includes('ring') ||
    signal.includes('notify') ||
    signal.includes('incoming') ||
    signal.includes('background')
  ) {
    return 'inspect-notification-path';
  }

  if (
    signal.includes('media') ||
    signal.includes('audio') ||
    signal.includes('video') ||
    signal.includes('permission')
  ) {
    return 'inspect-media-path';
  }

  if (
    signal.includes('signal') ||
    signal.includes('connect') ||
    signal.includes('answer')
  ) {
    return 'inspect-signaling-path';
  }

  if (
    signal.includes('session') ||
    signal.includes('end') ||
    signal.includes('route') ||
    signal.includes('lifecycle')
  ) {
    return 'inspect-session-lifecycle';
  }

  return 'observe-next-attempt';
};

const selectAuthority = (
  incident: CallsRuntimeIncident,
): CallsResponseAuthority =>
  incident.severity === 'critical'
    ? 'Repair Governor'
    : 'Calls Governor';

export function buildCallsIncidentResponsePlans(
  values: readonly unknown[],
): readonly CallsIncidentResponsePlan[] {
  const plans = new Map<
    string,
    CallsIncidentResponsePlan
  >();

  for (const value of values) {
    const incident = asRecord(
      value,
    ) as unknown as CallsRuntimeIncident;

    if (
      incident.status !== 'active' ||
      typeof incident.id !== 'string' ||
      typeof incident.key !== 'string'
    ) {
      continue;
    }

    const requiredAuthority =
      selectAuthority(incident);

    const plan: CallsIncidentResponsePlan =
      Object.freeze({
        id: `calls-response:${incident.key}`,
        incidentId: incident.id,
        incidentKey: incident.key,
        owner: 'Calls Governor',
        status:
          requiredAuthority === 'Repair Governor'
            ? 'awaiting-authority'
            : 'planned',
        recommendedAction:
          selectAction(incident),
        requiredAuthority,
        executionAllowed: false,
        retryEligible:
          incident.outcome === 'timeout',
        escalationOwner:
          incident.severity === 'critical'
            ? 'Repair Governor'
            : 'Verification Governor',
        verificationRequired: true,
        reasonCode: asString(
          incident.reasonCode,
          'No incident reason recorded',
        ),
        createdAt: asString(
          incident.lastObservedAt,
          'Unknown',
        ),
      });

    plans.set(incident.key, plan);
  }

  return Object.freeze([...plans.values()]);
}
